// Libs
import React, { useState, useCallback, useEffect } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Typography,
  Box,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  TextField,
  Chip,
} from "@mui/material";
import {
  PhotoIcon,
  TrashIcon,
  LinkIcon,
  EyeIcon,
  DocumentDuplicateIcon,
} from "@heroicons/react/24/outline";

// Local
import { ExistingMarketingAttachment } from "../../models/marketing-attachment";
import { useSiteKeyDocStore } from "../../store/site-key-doc";
import { DbRead, DbWrite } from "../../database";

interface EmailImagesManagerProps {
  open: boolean;
  onClose: () => void;
  onImageSelect?: (imageUrl: string) => void;
}

export const EmailImagesManager: React.FC<EmailImagesManagerProps> = ({
  open,
  onClose,
  onImageSelect,
}) => {
  const siteKey = useSiteKeyDocStore((state) => state.siteKeyDoc);

  const [images, setImages] = useState<ExistingMarketingAttachment[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [previewImage, setPreviewImage] =
    useState<ExistingMarketingAttachment | null>(null);

  // Load images when component opens
  useEffect(() => {
    if (open && siteKey) {
      loadImages();
    }
  }, [open, siteKey]);

  const loadImages = useCallback(async () => {
    if (!siteKey) return;

    setLoading(true);
    try {
      const emailImages = await DbRead.marketingAttachments.getAll(siteKey.id);
      setImages(emailImages);
    } catch (error) {
      console.error("Failed to load email images:", error);
      setUploadError("Failed to load images. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [siteKey]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      // Validate file types
      const validTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
        "image/webp",
      ];
      const invalidFiles = Array.from(files).filter(
        (file) => !validTypes.includes(file.type),
      );

      if (invalidFiles.length > 0) {
        setUploadError(
          "Please select only image files (JPEG, PNG, GIF, WebP).",
        );
        return;
      }

      // Validate file sizes (max 10MB each)
      const oversizedFiles = Array.from(files).filter(
        (file) => file.size > 10 * 1024 * 1024,
      );
      if (oversizedFiles.length > 0) {
        setUploadError("Each image must be smaller than 10MB.");
        return;
      }

      setSelectedFiles(files);
      setUploadError(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFiles || !siteKey) return;

    setUploading(true);
    setUploadError(null);

    try {
      for (const file of Array.from(selectedFiles)) {
        await DbWrite.marketingAttachments.upload({
          siteKey: siteKey.id,
          file,
          alt: file.name,
          description: `Uploaded marketing image: ${file.name}`,
        });
      }

      setSuccessMessage(
        `Successfully uploaded ${selectedFiles.length} image(s)!`,
      );
      setSelectedFiles(null);

      // Clear the file input
      const fileInput = document.getElementById(
        "email-image-upload",
      ) as HTMLInputElement;
      if (fileInput) {
        fileInput.value = "";
      }

      // Reload images
      await loadImages();
    } catch (error) {
      console.error("Failed to upload images:", error);
      setUploadError("Failed to upload images. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteImage = async (image: ExistingMarketingAttachment) => {
    if (!siteKey) return;

    if (
      !window.confirm(`Are you sure you want to delete "${image.filename}"?`)
    ) {
      return;
    }

    try {
      await DbWrite.marketingAttachments.delete({
        siteKey: siteKey.id,
        attachmentId: image.id,
      });

      setSuccessMessage("Image deleted successfully!");
      await loadImages();
    } catch (error) {
      console.error("Failed to delete image:", error);
      setUploadError("Failed to delete image. Please try again.");
    }
  };

  const handleCopyUrl = (url: string) => {
    navigator.clipboard
      .writeText(url)
      .then(() => {
        setSuccessMessage("Image URL copied to clipboard!");
      })
      .catch(() => {
        setUploadError("Failed to copy URL to clipboard.");
      });
  };

  const handleSelectImage = (image: ExistingMarketingAttachment) => {
    if (onImageSelect) {
      onImageSelect(image.url);
      onClose();
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { height: "80vh" },
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <PhotoIcon className="h-6 w-6" />
            Marketing Images
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {/* Upload Section */}
          <Box mb={3}>
            <Typography variant="h6" gutterBottom>
              Upload New Images
            </Typography>

            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <Button
                variant="outlined"
                component="label"
                startIcon={<PhotoIcon className="h-4 w-4" />}
                disabled={uploading}
              >
                Select Images
                <input
                  id="email-image-upload"
                  type="file"
                  hidden
                  multiple
                  accept="image/*"
                  onChange={handleFileSelect}
                />
              </Button>

              {selectedFiles && (
                <Button
                  variant="contained"
                  onClick={handleUpload}
                  disabled={uploading}
                  startIcon={
                    uploading ? <CircularProgress size={16} /> : undefined
                  }
                >
                  {uploading
                    ? "Uploading..."
                    : `Upload ${selectedFiles.length} Image(s)`}
                </Button>
              )}
            </Box>

            {selectedFiles && (
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Selected files:
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
                  {Array.from(selectedFiles).map((file, index) => (
                    <Chip
                      key={index}
                      label={file.name}
                      size="small"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Box>
            )}

            {uploadError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {uploadError}
              </Alert>
            )}

            {successMessage && (
              <Alert
                severity="success"
                sx={{ mt: 2 }}
                onClose={() => setSuccessMessage(null)}
              >
                {successMessage}
              </Alert>
            )}
          </Box>

          {/* Images Grid */}
          <Box>
            <Typography variant="h6" gutterBottom>
              Your Images ({images.length})
            </Typography>

            {loading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : images.length === 0 ? (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                py={8}
                sx={{
                  border: "2px dashed #e0e0e0",
                  borderRadius: 2,
                  backgroundColor: "#fafafa",
                }}
              >
                <PhotoIcon className="mb-4 h-16 w-16 text-gray-400" />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No images uploaded yet
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  textAlign="center"
                >
                  Upload your first image to get started with email marketing
                  visuals
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={2}>
                {images.map((image) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={image.id}>
                    <Card
                      sx={{
                        cursor: onImageSelect ? "pointer" : "default",
                        transition: "transform 0.2s, box-shadow 0.2s",
                        "&:hover": {
                          transform: onImageSelect ? "scale(1.02)" : "none",
                          boxShadow: onImageSelect ? 4 : 1,
                        },
                      }}
                      onClick={() => onImageSelect && handleSelectImage(image)}
                    >
                      <CardMedia
                        component="img"
                        height="150"
                        image={image.url}
                        alt={image.alt || image.filename}
                        sx={{ objectFit: "cover" }}
                      />
                      <CardContent sx={{ p: 1 }}>
                        <Typography variant="caption" component="div" noWrap>
                          {image.filename}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {(image.size / 1024).toFixed(1)} KB
                        </Typography>

                        <Box
                          display="flex"
                          justifyContent="space-between"
                          mt={1}
                        >
                          <Box display="flex" gap={0.5}>
                            <Tooltip title="Preview">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setPreviewImage(image);
                                }}
                              >
                                <EyeIcon className="h-4 w-4" />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="Copy URL">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCopyUrl(image.url);
                                }}
                              >
                                <DocumentDuplicateIcon className="h-4 w-4" />
                              </IconButton>
                            </Tooltip>
                          </Box>

                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteImage(image);
                              }}
                            >
                              <TrashIcon className="h-4 w-4" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>
            {onImageSelect ? "Cancel" : "Close"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Preview Dialog */}
      <Dialog
        open={!!previewImage}
        onClose={() => setPreviewImage(null)}
        maxWidth="md"
        fullWidth
      >
        {previewImage && (
          <>
            <DialogTitle>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography variant="h6">{previewImage.filename}</Typography>
                <Box display="flex" gap={1}>
                  <Tooltip title="Copy URL">
                    <IconButton
                      onClick={() => handleCopyUrl(previewImage.url)}
                      size="small"
                    >
                      <DocumentDuplicateIcon className="h-5 w-5" />
                    </IconButton>
                  </Tooltip>
                  {onImageSelect && (
                    <Tooltip title="Select Image">
                      <IconButton
                        onClick={() => handleSelectImage(previewImage)}
                        size="small"
                        color="primary"
                      >
                        <LinkIcon className="h-5 w-5" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            </DialogTitle>

            <DialogContent>
              <Box textAlign="center">
                <img
                  src={previewImage.url}
                  alt={previewImage.alt || previewImage.filename}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "70vh",
                    objectFit: "contain",
                  }}
                />
              </Box>

              <Box mt={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Image URL"
                  value={previewImage.url}
                  InputProps={{
                    readOnly: true,
                  }}
                  sx={{ mb: 1 }}
                />
                {previewImage.description && (
                  <Typography variant="body2" color="text.secondary">
                    {previewImage.description}
                  </Typography>
                )}
              </Box>
            </DialogContent>

            <DialogActions>
              <Button onClick={() => setPreviewImage(null)}>Close</Button>
              {onImageSelect && (
                <Button
                  variant="contained"
                  onClick={() => handleSelectImage(previewImage)}
                >
                  Select This Image
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
};

export default EmailImagesManager;
